/**
 * =============================================================================
 * ADMIN SECTION REGISTRY (ADR-025 §4 target IA — B-1)
 * =============================================================================
 *
 * Maps the §3.1 semantic section ids onto the §4 navigation structure and the
 * EXISTING panels each section mounts. B-1 was behaviour-preserving MOUNTING
 * only; B-2 (M9) completed the re-home — no file moves were needed (the
 * settings cards already lived at `components/Settings/`) and the media-row →
 * parent-question links landed via `SectionRenderContext` below. B-3 registers
 * Overview while preserving Questions as the OD-14 default landing.
 *
 * §4 groups are the optional visual affordance (frequency-weighted):
 * OPERATIONS (daily work) → INSIGHTS → MANAGEMENT → GOVERNANCE. B-3 adds
 * queue-unit pending badges and the persistent minimal launch-status chip.
 * =============================================================================
 */

import React from 'react';
import { Box } from '@bigcommerce/big-design';
import { QuestionQueue, type OpenQuestionIntent } from '../Questions/QuestionQueue';
import { AnalyticsDashboard } from '../Analytics';
import { LaunchModeCard, MediaSettingsCard } from '../Settings';
import { ParticipantLookup, ConsentInspection } from '../Support';
import { AuditPanel } from '../Audit';
import { MediaReviewPanel } from '../Media';
import { OverviewPanel } from '../Overview';
import type { PendingWorkSnapshot } from '../../hooks/usePendingWork';
import type {
  AdminFilteredViewIntents,
  AdminLocation,
  AdminSectionId,
  AdminSectionLocations,
} from './location';

/** §4 group headers (visual affordance only — not structure). */
export type AdminSectionGroup = 'OPERATIONS' | 'INSIGHTS' | 'MANAGEMENT' | 'GOVERNANCE';

/** The two settings cards that report unsaved-edit state to the shell (B-2). */
export type SettingsCardId = 'launchMode' | 'mediaSettings';

/**
 * Shell state handed to each section's `render` (B-2). Sections that need
 * none of it simply ignore the argument — the registry stays declarative and
 * the shell stays the single owner of cross-section state.
 */
export interface SectionRenderContext {
  /** Nonce-keyed explicit filtered destination; bare rail re-entry adds none. */
  filteredViewIntents: AdminFilteredViewIntents;
  /**
   * §3.1.6 / M8: pending open-question intent for the Questions section
   * (nonce-keyed so repeat dispatches re-fire under keep-alive); `null` until
   * the first questions-with-id navigation.
   */
  openQuestionIntent: OpenQuestionIntent | null;
  /**
   * Last explicitly committed semantic location for each mounted section.
   * The active location alone cannot carry keep-alive sub-state while another
   * section is visible. Nav re-entry restores this retained location; explicit
   * destinations still replace it.
   */
  retainedLocations: Readonly<AdminSectionLocations>;
  /**
   * §3.1.4 (B-2, `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`): per-card unsaved-edit
   * reporting from the Settings cards. The shell ORs the cards into the
   * Settings nav indicator + the expiry-warn-on-dirty affordance. Transition-
   * fired by the cards (their optional `onDirtyChange` contract).
   */
  reportSettingsCardDirty: (card: SettingsCardId, dirty: boolean) => void;
  location: AdminLocation;
  pending: PendingWorkSnapshot;
  navigate: (location: AdminLocation) => void;
  /** Panel state replacement without raising a filtered-destination intent. */
  updateLocation: (location: AdminLocation) => void;
  questionWorkspaceEnabled: boolean;
  /** Register the B-4 workspace's reply-draft navigation boundary. */
  registerNavigationGuard: (
    guard: (proceed: () => void) => void
  ) => () => void;
}

export interface AdminSectionDefinition {
  id: AdminSectionId;
  /** Nav-rail label (§4 wording). */
  navLabel: string;
  /** Section heading — the h1 that receives focus on activation (§3.1.3). */
  heading: string;
  group: AdminSectionGroup;
  /** Renders the section's panels (shell state via `SectionRenderContext`). */
  render: (context: SectionRenderContext) => React.ReactElement;
}

/**
 * Registered sections in §4 rail order. Overview is present, but Questions
 * remains the default landing.
 */
export const ADMIN_SECTIONS: readonly AdminSectionDefinition[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    heading: 'Overview',
    group: 'OPERATIONS',
    render: ({ pending, navigate }) => <OverviewPanel pending={pending} navigate={navigate} />,
  },
  {
    id: 'questions',
    navLabel: 'Questions',
    heading: 'Questions',
    group: 'OPERATIONS',
    render: ({
      openQuestionIntent,
      filteredViewIntents,
      location,
      updateLocation,
      questionWorkspaceEnabled,
      registerNavigationGuard,
      retainedLocations,
    }) => {
      const questionLocation = retainedLocations.questions;
      return (
        <QuestionQueue
          data-testid="question-queue"
          openQuestionIntent={openQuestionIntent}
          view={questionLocation?.view}
          viewIntentNonce={filteredViewIntents.questions?.nonce}
          workspaceEnabled={questionWorkspaceEnabled}
          selectedQuestionId={questionLocation?.questionId}
          isActive={location.section === 'questions'}
          onQuestionSelectionChange={(questionId) =>
            updateLocation({
              section: 'questions',
              ...(questionLocation?.view ? { view: questionLocation.view } : {}),
              ...(questionId ? { questionId } : {}),
            })
          }
          registerNavigationGuard={registerNavigationGuard}
        />
      );
    },
  },
  {
    id: 'media',
    navLabel: 'Media review',
    heading: 'Media review',
    group: 'OPERATIONS',
    render: ({ filteredViewIntents, location, retainedLocations }) => (
      <Box padding="medium" paddingBottom="none">
        <MediaReviewPanel
          view={retainedLocations.media?.view}
          viewIntentNonce={filteredViewIntents.media?.nonce}
          isActive={location.section === 'media'}
        />
      </Box>
    ),
  },
  {
    id: 'analytics',
    navLabel: 'Analytics',
    heading: 'Analytics',
    group: 'INSIGHTS',
    // §3.3 / M14: AnalyticsDashboard fetches on ITS mount — the shell only
    // mounts it on first activation, so no analytics request fires at startup.
    render: () => (
      <Box marginBottom="medium">
        <AnalyticsDashboard />
      </Box>
    ),
  },
  {
    id: 'support',
    navLabel: 'Support & Privacy',
    heading: 'Support & Privacy',
    group: 'MANAGEMENT',
    render: () => (
      <>
        <Box padding="medium" paddingBottom="none">
          <ParticipantLookup />
        </Box>
        <Box padding="medium" paddingBottom="none">
          <ConsentInspection />
        </Box>
      </>
    ),
  },
  {
    id: 'audit',
    navLabel: 'Audit',
    heading: 'Audit',
    group: 'GOVERNANCE',
    render: () => (
      <Box padding="medium" paddingBottom="none">
        <AuditPanel />
      </Box>
    ),
  },
  {
    id: 'settings',
    navLabel: 'Settings',
    heading: 'Settings',
    group: 'GOVERNANCE',
    // B-2 (§3.1.4): the cards report unsaved-edit transitions to the shell
    // via their OPTIONAL `onDirtyChange` — the legacy layout passes nothing
    // and renders these cards unchanged (rollback surface, M9).
    render: ({ reportSettingsCardDirty }) => (
      <>
        <Box padding="medium" paddingBottom="none">
          <LaunchModeCard
            onDirtyChange={(dirty) => reportSettingsCardDirty('launchMode', dirty)}
          />
        </Box>
        <Box padding="medium" paddingBottom="none">
          <MediaSettingsCard
            onDirtyChange={(dirty) => reportSettingsCardDirty('mediaSettings', dirty)}
          />
        </Box>
      </>
    ),
  },
];
